import { tv } from 'tailwind-variants';
import type { AuthUser } from '@/api/models';
import { nameOfOption, type NamedOption } from '../namedOption';
import { PRODUCT_FILTER_DESCRIPTORS } from '../productFilterDescriptors';
import type { FilterOptionsState } from './useFilterOptionsState';

/** A `{ id, name }` branch option for the branch field's picker. */
export type BranchFilterOption = NamedOption;

/**
 * Who says which branch the Products list's stock figures count
 * (bos-app#118), read from the session's role:
 *
 * - `pick`: an OrgAdmin picks one branch, or All branches, in the drawer.
 * - `fixed`: a BranchAdmin sees their own branch, read-only. bos-api counts
 *   the token's branch whatever the request says.
 * - `none`: no session, or no branch to show, so nothing is shown or sent.
 */
export type BranchScopeMode = 'pick' | 'fixed' | 'none';

export const getBranchScopeMode = (user: AuthUser | null): BranchScopeMode => {
    if (user?.role === 'OrgAdmin') {
        return 'pick';
    }
    return user?.role === 'BranchAdmin' && user.branchId ? 'fixed' : 'none';
};

/**
 * The branch field, on the filters drawer and the product details page alike,
 * by the session's role: an OrgAdmin's branch picker with its options, a
 * BranchAdmin's own branch as a read-only line, or nothing. `branchName` is
 * `null` until the branch list gives it.
 */
export type ProductBranchScope =
    | { mode: 'pick'; options: FilterOptionsState<BranchFilterOption> }
    | { mode: 'fixed'; branchName: string | null }
    | { mode: 'none' };

/**
 * Whose stock the figures count, as the Cost header's tooltip names it:
 * every branch, one named branch, or one branch whose name the branch list
 * hasn't given (still loading, or failed).
 */
export type StockFiguresScope =
    | { kind: 'allBranches' }
    | { kind: 'branch'; name: string }
    | { kind: 'unnamedBranch' };

export const toStockFiguresScope = (
    branchId: string | null,
    branches: readonly BranchFilterOption[],
): StockFiguresScope => {
    if (branchId === null) {
        return { kind: 'allBranches' };
    }
    const name = nameOfOption(branches, branchId);
    return name === null ? { kind: 'unnamedBranch' } : { kind: 'branch', name };
};

/** The branch the list asks for, and whether it is still being checked. */
export type AddressBranch = {
    branchId: string | null;
    /** The branch list hasn't said yet whether the address's branch exists. */
    isPending: boolean;
};

/**
 * The branch the list asks for, from the one in the page address.
 *
 * Only an OrgAdmin's is read. Anyone else's scope is their token's, and a
 * branch in the address (an OrgAdmin's link, opened by a BranchAdmin) would
 * only earn a 403, so it is left out.
 *
 * An OrgAdmin's is checked against the branch list first. One the list
 * doesn't hold, a deleted or archived branch in an old link, reads as All
 * branches rather than failing the page with the API's 404 or showing a
 * branch the picker can't. Until the list arrives the branch is pending, and
 * the list request waits instead of showing every branch's figures under one
 * branch's name. If the branch list failed, the branch goes as it is and the
 * API decides.
 */
export const resolveAddressBranch = (
    mode: BranchScopeMode,
    branchId: string | null,
    options: Pick<FilterOptionsState<BranchFilterOption>, 'items' | 'status'>,
): AddressBranch => {
    if (mode !== 'pick' || branchId === null) {
        return { branchId: null, isPending: false };
    }
    switch (options.status) {
        case 'loading':
            return { branchId, isPending: true };
        case 'failed':
            return { branchId, isPending: false };
        case 'ready':
            return {
                branchId: options.items.some((branch) => branch.id === branchId)
                    ? branchId
                    : null,
                isPending: false,
            };
    }
};

/** The branch picker's key for All branches. Branch ids are server-made, so no branch has it. */
export const ALL_BRANCHES_KEY = '__all-branches__';

/** The picker's selected key for a held branch; `null` is All branches. */
export const toBranchPickerKey = (branchId: string | null): string =>
    branchId ?? ALL_BRANCHES_KEY;

/** Narrows the key the picker emitted back to a branch, `null` for All branches. */
export const fromBranchPickerKey = (
    key: string | number | null,
): string | null =>
    key === null || key === ALL_BRANCHES_KEY
        ? PRODUCT_FILTER_DESCRIPTORS.branch.cleared.branchId
        : String(key);

/**
 * A screen's own words for its branch field (bos-app#138). "All branches" and
 * the BranchAdmin's "Your branch" are the field's own, the same on every
 * screen.
 */
export type BranchScopeCopy = {
    label: string;
    /** Under an OrgAdmin's picker, where the screen explains it. */
    pickerDescription?: string;
    /** Under a BranchAdmin's read-only line, where the screen explains it. */
    lineDescription?: string;
};

/**
 * Where a screen puts its branch field: `stacked`, full width with its label
 * above, heading the filters drawer; or `inline`, its label beside it, in the
 * product details header beside Edit.
 */
export type BranchScopeLayout = 'stacked' | 'inline';

/**
 * Each layout's placement, shared by the picker and the read-only line: the
 * field's own classes, its control's, and the picker's wrapper around it and
 * its loading status. Inline, the wrapper steps aside, so the picker and its
 * status sit in the header's own row.
 */
export const branchScopeLayout = tv({
    slots: { wrapper: '', field: '', control: '' },
    variants: {
        layout: {
            stacked: { wrapper: 'flex flex-col gap-1', field: 'gap-2' },
            inline: {
                wrapper: 'contents',
                field: 'w-fit flex-row items-center gap-2',
                control: 'w-50',
            },
        },
    },
});

/** Stacked, the field fills the drawer's width; inline, it takes its own. */
export const isBranchScopeFullWidth = (layout: BranchScopeLayout): boolean =>
    layout === 'stacked';
